// Global React Error Boundary.
// Catches unhandled render/lifecycle errors and reports them via the
// session-independent logClientError (authenticated → edge fallback → queue).
// Wrapped around the whole app in main.tsx.

import React, { Component } from 'react';
import { logClientError } from '@/lib/errorLogger';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  logged: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, logged: false };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  async componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (this.state.logged) return;
    this.setState({ logged: true });

    // logClientError never throws, but guard anyway — componentDidCatch must not throw.
    try {
      await logClientError({
        error_message: error.message,
        stack_trace: (error.stack ?? '') + '\n\nComponent stack:\n' + (info.componentStack ?? ''),
        error_type: 'crash',
      });
    } catch {
      // Never throw from componentDidCatch
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" /> Something went wrong
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              An unexpected error occurred and has been automatically reported. Please try refreshing the page.
            </p>
            <Button onClick={() => window.location.reload()} className="gap-2 w-full">
              <RefreshCw className="h-4 w-4" /> Refresh Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
}

export default ErrorBoundary;
